import AdminHeader from "components/Headers/AdminHeader";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Tooltip,
  InputGroup,
  Input,
  InputGroupAddon,
  InputGroupText,
  Label,
} from "reactstrap";
import { toCamelCase } from "components/CommonComponents/CommonFunctions";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import CustomModal from "components/CommonComponents/CustomeModal";
import { useDispatch, useSelector } from "react-redux";
import { channelPartnerEnrolledCourseList } from "constants/course";
import {
  getChannelPartnerEnrolledCourseList,
  channelPartnerAddCourseRating,
} from "action/course";
import CustomPagination from "components/CommonComponents/ReactTable/CustomPagination";

// const COURSE_TYPE_OPTIONS = [
//   { value: "all", label: "All Course Types" },
//   { value: "offline", label: "Offline Training" },
//   { value: "online", label: "Live Online" },
//   { value: "recorded", label: "Recorded Course" },
// ];

const ChannelPartnerMyCourses = () => {
  const { notify } = useContext(NotificationContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isFirstSearchRender = useRef(true);

  const [courseListState, setCourseListState] = useState({
    isLoading: true,
    error: "",
    noEnrolledCourse: false,
  });

  const [filterState, setFilterState] = useState({
    searchName: "",
    courseType: ["All"],
    filterParams: {
      filters: [],
      searchString: "",
    },
  });

  const [showFilters, setShowFilters] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [tooltipOpen, setTooltipOpen] = useState({});
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [search, setSearch] = useState("");

  const courseListData = useSelector(
    (state) => state.course.channelPartnerEnrolledCourseList,
  );

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackData, setFeedbackData] = useState({
    course: null,
    courseId: null,
    rating: 0,
    feedback: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [quickViewCourse, setQuickViewCourse] = useState(null);
  const [enrollMentCourseData, setEnrollMentCourseData] = useState(null);

  // ---- Data comes straight from the API response now ----
  // API shape: { data: { totalRecords, noOfRecordsPerPage, courses: [...] } }
  const courses = courseListData?.data?.courses || [];
  const totalRecords = courseListData?.data?.totalRecords || 0;
  const recordsPerPage = courseListData?.data?.noOfRecordsPerPage || 10;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  const fetchEnrolledCourseList = useCallback(() => {
    setCourseListState((prev) => ({ ...prev, isLoading: true, error: "" }));

    getChannelPartnerEnrolledCourseList(
      dispatch,
      pageNum,
      filterState.filterParams,
      debouncedSearch,
      {
        sortBy: "CreatedAt",
        sortType: "desc",
      },
    );
  }, [dispatch, pageNum, filterState.filterParams, debouncedSearch]);

  useEffect(() => {
    if (courseListData?.code === 200) {
      // const hasCourses = (courseListData?.data?.courses?.length || 0) > 0;
      const hasNoCourses = (courseListData?.data?.courses?.length || 0) === 0;
      
      setCourseListState({
        isLoading: false,
        error: "",
        noEnrolledCourse: hasNoCourses,
      });
    } else if (courseListData?.code || courseListData?.message) {
      setCourseListState({
        isLoading: false,
        error:
          courseListData?.message ||
          "Failed to load enrolled courses! Please try again.",
        noEnrolledCourse: false,
      });
    } else {
      // Handle case when no response data exists
      setCourseListState({
        isLoading: false,
        error: "",
        noEnrolledCourse: true,
      });
    }
  }, [courseListData]);

  // const openFeedbackModal = (course) => {
  //   setFeedbackData({
  //     course,
  //     courseId: course?.courseId,
  //     rating: 0,
  //     feedback: "",
  //   });
  //   setIsSubmitting(false);
  //   setIsFeedbackModalOpen(true);
  // };

  // const openQuickView = (course) => {
  //   fetchEnrolledCourseDetails(course?.courseId);
  //   setQuickViewCourse(course);
  //   setIsQuickViewOpen(true);
  // };

  const closeQuickView = () => setIsQuickViewOpen(false);

  const handleSubmitRating = async () => {
    if (feedbackData.rating === 0) {
      notify("danger", "Please select a rating.");
      return;
    }
    if (!feedbackData.courseId) {
      notify("danger", "Course ID is missing.");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      courseID: feedbackData.courseId,
      rating: String(feedbackData.rating),
      review: feedbackData.feedback.trim(),
    };

    try {
      const response = await channelPartnerAddCourseRating(payload);

      if (response?.code === 200) {
        notify(
          "success",
          response?.message || "Course rating submitted successfully.",
        );
        setIsFeedbackModalOpen(false);
        setFeedbackData({ course: null, courseId: null, rating: 0, feedback: "" });
        fetchEnrolledCourseList();
      } else {
        const errorMessage = Array.isArray(response?.message)
          ? response.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : response?.message || "Failed to submit course rating.";
        notify("danger", errorMessage);
      }
    } catch (error) {
      notify(
        "danger",
        error?.response?.data?.message || error?.message || "Failed to submit course rating.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // const fetchEnrolledCourseDetails = async (courseId) => {
  //   try {
  //     const res = await channelPartnerCourseEnrollmentDetails(courseId);
  //     if (res?.code === 200) {
  //       setEnrollMentCourseData(res?.data);
  //     } else {
  //       notify("danger", res?.message || "Failed to load course overview");
  //     }
  //   } catch (error) {
  //     notify("danger", error?.message || "Failed to load course overview");
  //   }
  // };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setFilterState((prev) => ({ ...prev, searchName: value }));
    setSearch(value);
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchEnrolledCourseList();
    return () => {
      dispatch({ type: channelPartnerEnrolledCourseList, payload: {} });
    };
  }, [pageNum, filterState.filterParams, debouncedSearch, fetchEnrolledCourseList]);

  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    setPageNum(1);
    setFilterState((prev) => {
      const updatedState = { ...prev, searchName: debouncedSearch };
      return { ...updatedState, filterParams: buildFilterParams(updatedState) };
    });
  }, [debouncedSearch]);

  const buildFilterParams = (state = filterState) => {
    const filters = [];
    if (state.courseType.length) {
      const courseTypes = state.courseType.filter((v) => v !== "All");
      if (courseTypes.length) {
        filters.push({
          field: "CourseType",
          condition: "in",
          filterValues: courseTypes.map((v) => v.toUpperCase()),
        });
      }
    }
    return { filters: filters.length ? JSON.stringify(filters) : "" };
  };

  // Course-type dropdown drives a real API filter (server-side), not a client-side filter
  // const handleCourseTypeChange = (option) => {
  //   setCourseType(option);
  //   setPageNum(1);
  //   setFilterState((prev) => {
  //     const updatedState = {
  //       ...prev,
  //       courseType: option.value === "all" ? ["All"] : [option.value],
  //     };
  //     return { ...updatedState, filterParams: buildFilterParams(updatedState) };
  //   });
  // };

  const toggleTooltip = (key) => {
    setTooltipOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleNavigation = (course) => {
    navigate(`/channel-partner/my-courses/${urlEncodedId(course?.courseId)}/details`);
  };

  const getCourseTypeInfo = (course) => {
    const deliveryMode = (course?.contentDeliveryMode || "").toLowerCase();
    const type = (course?.courseType || "").toLowerCase();

    switch (deliveryMode) {
      case "offline":
        return { label: "Offline", icon: "ni ni-building", className: "offline" };
      case "both":
        return { label: "Hybrid", icon: "ni ni-app", className: "hybrid" };
      default:
        // Fallback to courseType if deliveryMode is ONLINE or empty
        switch (type) {
          case "recorded":
            return { label: "Recorded", icon: "ni ni-button-play", className: "recorded" };
          default:
            return { label: "Online", icon: "ni ni-world", className: "live" };
        }
    }
  };

  const handleClearSearch = () => {
    setPageNum(1);
    setSearch("");
    setDebouncedSearch("");
    setFilterState((prev) => {
      const updatedState = { ...prev, searchName: "" };
      return { ...updatedState, filterParams: buildFilterParams(updatedState) };
    });
  };

  // const handleClearLocalFilters = () => {
  //   setCourseType(COURSE_TYPE_OPTIONS[0]);
  //   setFilterState((prev) => {
  //     const updatedState = { ...prev, courseType: ["All"] };
  //     return { ...updatedState, filterParams: buildFilterParams(updatedState) };
  //   });
  //   handleClearSearch();
  // };

  return (
    <>
      <AdminHeader />
      <Container fluid className="mt--6 mb-4">
        <div className="d-flex align-items-center mb-4">
          <h3 className="mb-0 mt-4 text-white-input flex-shrink-0">
            Let's start learning
          </h3>
          <div className="d-flex align-items-center justify-content-end justify-content-md-start ml-auto flex-nowrap">
            <InputGroup className="mr-4 w-80 search-bottom-border">
              <Input
                type="text"
                placeholder="Search title, category"
                className="text-white-input"
                value={filterState.searchName}
                onChange={handleSearchChange}
              />
              <InputGroupAddon addonType="append">
                <InputGroupText className="search-icons">
                  {filterState.searchName && (
                    <i className="fa fa-times clear-search" onClick={handleClearSearch} />
                  )}
                  <i className="fa fa-search" />
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          </div>
        </div>

        <div className="d-lg-none d-flex justify-content-end mt-5 mb-1 w-100">
          <Button
            color="primary"
            className="btn-deep-blue"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </div>

        <Row className="mt-5">
          <Col xl="12" lg="12" md="12">
            {/* Loading State */}
            {courseListState.isLoading ? (
              <div className="loaderContainer no-courses-container">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : courseListState.error ? (
              /* Error State */
              <div className="loaderContainer error-style no-courses-container">
                <div className="error-icon">
                  <i className="fa-solid fa-circle-exclamation"></i>
                </div>
                <span>{courseListState.error}</span>
              </div>
            ) : courseListState.noEnrolledCourse ? (
              /* No Enrolled Courses State - User hasn't enrolled in any course yet */
              <div className="loaderContainer empty-style no-courses-container">
                <div className="empty-icon">
                  <i className="fa-solid fa-book-open"></i>
                </div>
                <span className="small-text">
                Your trainees aren't enrolled in any courses yet.
                </span>
                <span>Explore courses to enroll them. </span>
                <button
                  type="button"
                  className="btn btn-primary btn-sm mt-3"
                  onClick={() => navigate("/channel-partner/course")}
                >
                  Explore Courses
                  <i className="fa-solid fa-arrow-right ms-2"></i>
                </button>
              </div>
            ) : courses.length === 0 ? (
              /* No Results Found - Search returned no matching courses */
              <div className="loaderContainer empty-style no-courses-container">
                <div className="empty-icon">
                  <i className="fa-solid fa-circle-info"></i>
                </div>
                <span>No courses found matching your search.</span>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm mt-3"
                  onClick={handleClearSearch}
                >
                  <i className="fa fa-undo mr-2"></i>
                  Clear Search
                </button>
              </div>
            ) : (
              /* Courses Grid */
              <>
                <div className="my-courses-grid">
                  {courses.map((course) => (
                    <div key={course.courseId} className="my-course-card-wrapper">
                      <Card className="my-course-card mb-1 cursor">
                        <div className="my-course-image-wrapper">
                          {course.mediaUrl?.match(/\.(mp4|webm|ogg|mov|m4v)$/i) ? (
                            <video
                              className="course-image"
                              src={course.mediaUrl}
                              muted
                              preload="metadata"
                              playsInline
                              disablePictureInPicture
                              controls={false}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <img
                              src={
                                course.mediaUrl ||
                                "https://tms-stage-bucket.s3.ap-south-1.amazonaws.com/courseThumbNailImages/default_course_iamge.png"
                              }
                              alt={course.courseName}
                              className="course-image"
                            />
                          )}

                          <span className="course-overview__badges">
                            {(() => {
                              const typeInfo = getCourseTypeInfo(course);
                              return (
                                <span className={`course-type-pill ${typeInfo.className}`}>
                                  <i className={typeInfo.icon} />
                                  {typeInfo.label}
                                </span>
                              );
                            })()}
                          </span>
                        </div>

                        <CardBody className="pt-3 pb-1 ml--1 mr--1">
                          <div className="small-text text-default font-weight-bold">
                            <div
                              id={`courseTitleTooltip-${course.courseId}`}
                              className="course-title-ellipsis"
                            >
                              {course.courseName}
                            </div>
                            <Tooltip
                              className="qb-tooltip"
                              placement="auto"
                              delay={{ show: 150, hide: 0 }}
                              target={`courseTitleTooltip-${course.courseId}`}
                              isOpen={tooltipOpen[`title-${course.courseId}`]}
                              toggle={() => toggleTooltip(`title-${course.courseId}`)}
                              autohide={false}
                            >
                              {course.courseName}
                            </Tooltip>
                          </div>

                          <div className="d-flex justify-content-between mt-3">
                            <div>
                              <div className="text-muted small">Level</div>
                              <div className="xs-text">{toCamelCase(course.courseLevel)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-muted small">Category</div>
                              <div>
                                <span
                                  id={`courseCategoryTooltip-${course.courseId}`}
                                  className="course-category-ellipsis xs-text"
                                >
                                  {course.courseCategory}
                                </span>
                              </div>
                              <Tooltip
                                className="qb-tooltip"
                                placement="auto"
                                delay={{ show: 150, hide: 0 }}
                                isOpen={tooltipOpen[`category-${course.courseId}`]}
                                target={`courseCategoryTooltip-${course.courseId}`}
                                toggle={() =>
                                  setTooltipOpen((prev) => ({
                                    ...prev,
                                    [`category-${course.courseId}`]: !prev[`category-${course.courseId}`],
                                  }))
                                }
                                autohide={false}
                              >
                                {course.courseCategory}
                              </Tooltip>
                            </div>
                          </div>

                          {/* UPDATED: Trainees & Batch Details with unique class names */}
                          <div className="cp-mycourses-batch-section">
                            <div className="cp-mycourses-batch-title">Trainees & Batch Details</div>
                            <div className="cp-mycourses-batch-stats">
                              <div className="cp-mycourses-batch-stat-item">
                                <i className="fa fa-school" />
                                <span className="cp-mycourses-batch-stat-value">
                                  {course.purchasedBatchesCount || "-"}
                                </span>
                                <span className="cp-mycourses-batch-stat-label">
                                  Batch{course.purchasedBatchesCount > 1 ? 'es' : ''}
                                </span>
                              </div>
                              <div className="cp-mycourses-batch-stat-item">
                                <i className="fa fa-users" />
                                <span className="cp-mycourses-batch-stat-value">
                                  {course.enrolledTrainees || 0}
                                </span>
                                <span className="cp-mycourses-batch-stat-label">
                                  Trainee{course.enrolledTrainees > 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="price-section d-flex justify-content-between align-items-center w-100 mt-3 pt-2">
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNavigation(course);
                              }}
                              className="course-primary-btn m-0"
                            >
                              View Course
                            </Button>

                            <Button
                              color="link"
                              className="text-deep-blue p-0 font-weight-bold"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/channel-partner/course/${urlEncodedId(course?.courseId)}`);
                              }}
                            >
                              Enroll
                              <i className="fas fa-arrow-right ml-1" />
                            </Button>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <CustomPagination
                    totalPages={totalPages}
                    activePage={pageNum}
                    setPageNum={setPageNum}
                  />
                )}
              </>
            )}
          </Col>
        </Row>

        {/* Feedback Modal */}
        <CustomModal
          isOpen={isFeedbackModalOpen}
          onClose={() => setIsFeedbackModalOpen(false)}
          title={
            <>
              <i className="fa fa-graduation-cap mr-2"></i>
              Course Completion Feedback
            </>
          }
          isRatingModal={true}
        >
          <div>
            <h4>{feedbackData.course?.courseName}</h4>
            <Label className="small-label mb-4">
              Rate your overall learning experience with this course.
            </Label>

            <div className="d-flex align-items-center justify-content-between mb-4">
              <div>
                <h4>
                  Rating
                  <span className="text-danger"> *</span>
                </h4>
                <div className="feedback-rating-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i
                      key={star}
                      className={`fa fa-star mr-1 ${
                        star <= feedbackData.rating ? "star-yellow" : "star-light"
                      }`}
                      onClick={() =>
                        !isSubmitting &&
                        setFeedbackData((prev) => ({ ...prev, rating: star }))
                      }
                    />
                  ))}
                </div>
              </div>
            </div>

            <div>
              <h4>
                Feedback <span className="text-muted small">(Optional)</span>
              </h4>
              <textarea
                className="form-control course-feedback-input"
                maxLength={500}
                placeholder="Share your feedback about this course..."
                value={feedbackData.feedback}
                onChange={(e) =>
                  setFeedbackData((prev) => ({ ...prev, feedback: e.target.value }))
                }
                disabled={isSubmitting}
              />
            </div>

            <div className="feedback-actions mt-4 d-flex justify-content-end">
              <Button
                type="button"
                color="secondary"
                className="feedback-btn"
                disabled={isSubmitting}
                onClick={() => setIsFeedbackModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                color="deep-blue"
                className="feedback-btn"
                disabled={feedbackData.rating === 0 || isSubmitting}
                onClick={handleSubmitRating}
              >
                {isSubmitting ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          </div>
        </CustomModal>

        {/* Quick View Modal */}
        <CustomModal
          isOpen={isQuickViewOpen}
          onClose={closeQuickView}
          title={
            <>
              <i className="fa-solid fa-circle-info mr-2"></i>
              Enrollment details and training information
            </>
          }
          size="xl"
        >
          {enrollMentCourseData && (
            <div className="course-overview-modal">
              <div className="course-overview__hero">
                <div className="course-overview__hero-content">
                  <h2 className="course-overview__title">
                    {enrollMentCourseData.course?.name || quickViewCourse?.courseName}
                  </h2>

                  <div className="course-overview__batch-list">
                    <div className="course-overview__batch-row">
                      <span className="batch-label">Batch</span>
                      <span className="batch-value">
                        <strong>
                          {toCamelCase(enrollMentCourseData.batch?.name) || "-"}
                        </strong>
                        {enrollMentCourseData.batch?.code && (
                          <span className="batch-code">
                            {enrollMentCourseData.batch.code}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="course-overview__batch-row">
                      <span className="batch-label">Enrolled On</span>
                      <span className="batch-value">
                        <strong>{enrollMentCourseData.enrolled_on || "-"}</strong>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="course-overview__fee-card">
                <div className="course-overview__fee-icon">
                  <i className="fa-solid fa-indian-rupee-sign" />
                </div>
                <div className="course-overview__fee-content">
                  <span className="course-overview__fee-label">Course Fee</span>
                  <strong className="course-overview__fee-value">
                    ₹{Number(enrollMentCourseData.course?.price || 0).toLocaleString("en-IN")}
                  </strong>
                </div>
              </div>

              {(enrollMentCourseData.course_completion_date ||
                enrollMentCourseData.certificate?.download_url) && (
                <div className="course-overview__completion-section">
                  <div className="course-overview__section-header">
                    <div className="course-overview__section-icon">
                      <i className="ni ni-trophy" />
                    </div>
                    <div>
                      <h4>Completion & Certificate</h4>
                      <span>Course completion status and certificate</span>
                    </div>
                  </div>

                  <div className="course-overview__completion-grid">
                    {enrollMentCourseData.course_completion_date && (
                      <div className="course-overview__completion-item">
                        <div className="completion-icon completed">
                          <i className="fa-solid fa-check" />
                        </div>
                        <div className="completion-content">
                          <span>Course Completed</span>
                          <strong>{enrollMentCourseData.course_completion_date}</strong>
                        </div>
                      </div>
                    )}

                    {enrollMentCourseData.certificate?.download_url && (
                      <div className="course-overview__completion-item">
                        <div className="completion-icon certificate">
                          <i className="fa-solid fa-certificate" />
                        </div>
                        <div className="completion-content">
                          <span>Certificate</span>
                          <a
                            href={enrollMentCourseData.certificate.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="certificate-download-link"
                          >
                            <i className="fa-solid fa-download mr-1" />
                            Download Certificate
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {enrollMentCourseData.invoice?.generated &&
                enrollMentCourseData.invoice?.download_url && (
                  <div className="course-overview__completion-item">
                    <div className="course-overview__section-header">
                      <div className="course-overview__section-icon">
                        <i className="fa-solid fa-file-invoice" />
                      </div>
                      <div>
                        <h4>Invoice</h4>
                        <a
                          href={enrollMentCourseData.invoice.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="invoice-download-link"
                        >
                          <i className="fa-solid fa-download mr-1" />
                          Download Invoice
                        </a>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          )}
        </CustomModal>
      </Container>
    </>
  );
};

export default ChannelPartnerMyCourses;